/**
 * Service worker registration + update lifecycle.
 *
 * Kept out of the React tree on purpose: registration should start as early as
 * possible (main.jsx, before the first render) and must survive a re-render of
 * whatever component happens to show the update banner.
 *
 * ── Why every URL is derived from BASE_URL ───────────────────────────────────
 * `import.meta.env.BASE_URL` is Vite's `base`, inlined at build time. It is "/"
 * in production (CodeBuild sets VITE_BASE_URL=/) and "/wadakaroo/" in local
 * dev. Hardcoding either would register the worker outside its own scope in the
 * other environment, which the browser rejects outright.
 *
 * ── updateViaCache: 'none' ───────────────────────────────────────────────────
 * Without it the browser may serve sw.js itself from the HTTP cache for up to
 * 24h, so a shipped fix to the worker would not reach installed phones for a
 * day. 'none' forces the script itself to be revalidated on every update check.
 */

const SW_URL = `${import.meta.env.BASE_URL}sw.js`;
const SCOPE  = import.meta.env.BASE_URL;

// A page reload is triggered exactly once when the new worker takes control.
// Without this guard, a worker that activates while several tabs are open can
// bounce them all repeatedly.
let reloading = false;

/**
 * Register the worker.
 *
 * @param {object}   options
 * @param {Function} options.onUpdateReady called with an `activate()` function
 *        when a newer worker is installed and waiting. Invoking activate()
 *        tells the worker to skipWaiting; the page then reloads on
 *        controllerchange.
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function registerServiceWorker({ onUpdateReady } = {}) {
    if (!('serviceWorker' in navigator)) return null;

    // Off in `npm run dev` — a caching worker in front of Vite's HMR makes for
    // extremely confusing debugging. `npm run preview` builds in production
    // mode, so that is where to exercise the PWA locally.
    if (!import.meta.env.PROD) return null;

    try {
        const registration = await navigator.serviceWorker.register(SW_URL, {
            scope: SCOPE,
            updateViaCache: 'none',
        });

        const notify = (worker) => {
            if (!worker || typeof onUpdateReady !== 'function') return;
            onUpdateReady(() => worker.postMessage({ type: 'SKIP_WAITING' }));
        };

        // A worker can already be waiting when this runs — e.g. the user closed
        // the app before accepting the previous update prompt.
        if (registration.waiting && navigator.serviceWorker.controller) {
            notify(registration.waiting);
        }

        registration.addEventListener('updatefound', () => {
            const installing = registration.installing;
            if (!installing) return;
            installing.addEventListener('statechange', () => {
                // `controller` is null on the very first install (nothing was
                // controlling the page before), which is an install, not an
                // update — prompting there would be noise.
                if (installing.state === 'installed' && navigator.serviceWorker.controller) {
                    notify(installing);
                }
            });
        });

        navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (reloading) return;
            reloading = true;
            window.location.reload();
        });

        // Workshop phones keep this app open for days at a time, so the
        // browser's own update check on navigation never fires. Re-check
        // whenever the app comes back to the foreground, throttled to once an
        // hour so returning to the app between jobs isn't a network request
        // every time.
        let lastCheck = Date.now();
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState !== 'visible') return;
            if (Date.now() - lastCheck < 60 * 60 * 1000) return;
            lastCheck = Date.now();
            registration.update().catch(() => { /* offline; try again later */ });
        });

        return registration;
    } catch {
        // A failed registration must never break the app — it only costs the
        // offline shell and installability.
        return null;
    }
}

export default registerServiceWorker;
